import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { Grid } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DyanamicDropdown from 'components/Elements/DynamicDropdown';
import i18n from 'i18n';
import { useFilters } from '../FilterContext';
import { useFilterInitialization } from './hooks/useFilterInitialization';

// Quick Filter Options - can be extended with more options in the future.
// `id` is the STABLE identifier used for all logic/comparisons; `name` is
// translated at access time via a getter (module scope can't use the t hook,
// and i18n.t must not run at module load).
export const QUICK_FILTER_OPTIONS = {
	SAP_ERRORS_ONLY: { id: 'sap_errors_only', get name() { return i18n.t("option.showSapErrorsOnly"); } },
	FUNCTIONAL_LOCATION_ONLY: { id: 'functional_location_only', get name() { return i18n.t("option.functionalLocationOnly"); } },
	NEW_DEFECTS_ONLY: { id: 'new_defects_only', get name() { return i18n.t("option.showNewDefectsOnly"); } },
};

export const QuickFilter = () => {
	const { t } = useTranslation();
	const { filterValues, updateFilter, config, initialValues, persistence, siteAppId, storageKey } = useFilters();

	const hasIntegrationInstances =
		config.filters?.quickFilter?.hasIntegrationInstances || false;
	const canShowFunctionalLocationOnly =
		config.filters?.quickFilter?.showFunctionalLocationOnly || false;
	const canShowNewDefectsOnly =
		config.filters?.quickFilter?.showNewDefectsOnly || false;

	const filterKey = 'quickFilter';
	const multiSelect = true; // Always multi-select for quick filters
	const initialSelectionAppliedRef = useRef(false);

	// Get available options based on context (e.g., SAP integration)
	const availableOptions = useMemo(() => {
		const options = [];

		// Only show SAP Errors option if SAP integration is active
		if (hasIntegrationInstances) {
			options.push(QUICK_FILTER_OPTIONS.SAP_ERRORS_ONLY);
		}

		// Only show Functional Location Only option when enabled for the site
		if (canShowFunctionalLocationOnly) {
			options.push(QUICK_FILTER_OPTIONS.FUNCTIONAL_LOCATION_ONLY);
		}

		if (canShowNewDefectsOnly) {
			options.push(QUICK_FILTER_OPTIONS.NEW_DEFECTS_ONLY);
		}

		// Future options can be added here
		// if (someOtherCondition) {
		//   options.push(QUICK_FILTER_OPTIONS.SOME_OTHER_OPTION);
		// }

		return options;
	}, [hasIntegrationInstances, canShowFunctionalLocationOnly]);

	useFilterInitialization(filterKey, availableOptions, {
		waitForData: true,
		persistAll: true, // Persist to URL and localStorage
		multiSelect,
		getDefaultValue: () => {
			// Default to no filters selected
			return [];
		},
		getParams: (value) => {
			const params = {
				showSAPErrorsOnly: false,
				showFunctionalLocationOnly: false,
				showNewDefectsOnly: false,
			};
			if (!value || value.length === 0) return params;

			const selectedIds = value.map(option => option.id);

			// Set params based on selected options
			if (selectedIds.includes(QUICK_FILTER_OPTIONS.SAP_ERRORS_ONLY.id)) {
				params.showSAPErrorsOnly = true;
			}

			if (selectedIds.includes(QUICK_FILTER_OPTIONS.FUNCTIONAL_LOCATION_ONLY.id)) {
				params.showFunctionalLocationOnly = true;
			}

			if (selectedIds.includes(QUICK_FILTER_OPTIONS.NEW_DEFECTS_ONLY.id)) {
				params.showNewDefectsOnly = true;
			}

			// Future options can add their params here
			// if (selectedIds.includes(QUICK_FILTER_OPTIONS.SOME_OTHER_OPTION.id)) {
			//   params.someOtherParam = true;
			// }

			return params;
		},
		// QuickFilter is a Defects-list-page concern; the report DTOs don't
		// have showSAPErrorsOnly / showFunctionalLocationOnly / showNewDefectsOnly
		// fields, and buildReportFilterContext doesn't strip booleans, so the
		// false defaults would otherwise bleed into the report filterContext.
		getReportParams: () => ({}),
	});

	const handleChange = (selection, persist = true) => {
		const selectedIds = selection.map(option => option.id);
		const params = {
			showSAPErrorsOnly: false,
			showFunctionalLocationOnly: false,
			showNewDefectsOnly: false,
		};

		// Set params based on selected options
		if (selectedIds.includes(QUICK_FILTER_OPTIONS.SAP_ERRORS_ONLY.id)) {
			params.showSAPErrorsOnly = true;
		}

		if (selectedIds.includes(QUICK_FILTER_OPTIONS.FUNCTIONAL_LOCATION_ONLY.id)) {
			params.showFunctionalLocationOnly = true;
		}

		if (selectedIds.includes(QUICK_FILTER_OPTIONS.NEW_DEFECTS_ONLY.id)) {
			params.showNewDefectsOnly = true;
		}

		// Future options can add their params here

		// Empty reportParams: QuickFilter is a list-page concern and doesn't
		// contribute to the report DTO. Without this, the showSAPErrorsOnly /
		// showFunctionalLocationOnly / showNewDefectsOnly false defaults would
		// bleed into the report filterContext via the .params fallback in
		// buildReportFilterContext.
		updateFilter(filterKey, selection, params, persist, {});
	};

	useEffect(() => {
		if (initialSelectionAppliedRef.current) return;

		// Check if quickFilterClear parameter is present (from analytics navigation with asset)
		const urlParams = new URLSearchParams(window.location.search);
		const shouldClear = urlParams.get('quickFilterClear') === 'true';

		if (shouldClear) {
			// Clear the quick filter from localStorage and state
			if (persistence && persistence.clearFilter) {
				persistence.clearFilter(filterKey);
			}
			// Set filter to empty array (no filters selected)
			handleChange([], false);
			initialSelectionAppliedRef.current = true;
			return;
		}

		const storedValue = initialValues[filterKey];
		let storedIds = Array.isArray(storedValue)
			? storedValue
			: storedValue
			? [storedValue]
			: [];

		if (storedIds.length === 0) {
			const urlIds = urlParams.getAll(filterKey);
			if (urlIds.length > 0) {
				storedIds = urlIds;
			}
		}

		if (storedIds.length === 0) {
			initialSelectionAppliedRef.current = true;
			return;
		}

		if (availableOptions.length === 0) return;

		const matchedOptions = storedIds
			.map((id) => availableOptions.find((option) => option.id === id))
			.filter(Boolean);

		if (matchedOptions.length === 0) return;

		const currentSelection = filterValues[filterKey]?.value || [];
		const alreadySelected = storedIds.every((id) =>
			currentSelection.some((item) => item.id === id)
		);

		if (alreadySelected) {
			initialSelectionAppliedRef.current = true;
			return;
		}

		const params = {
			showSAPErrorsOnly: matchedOptions.some(
				(option) => option.id === QUICK_FILTER_OPTIONS.SAP_ERRORS_ONLY.id
			),
			showFunctionalLocationOnly: matchedOptions.some(
				(option) =>
					option.id === QUICK_FILTER_OPTIONS.FUNCTIONAL_LOCATION_ONLY.id
			),
			showNewDefectsOnly: matchedOptions.some(
				(option) => option.id === QUICK_FILTER_OPTIONS.NEW_DEFECTS_ONLY.id
			),
		};

		handleChange(matchedOptions, true);
		initialSelectionAppliedRef.current = true;
	}, [
		availableOptions,
		filterValues,
		filterKey,
		initialValues,
		persistence,
		siteAppId,
		storageKey,
		updateFilter,
	]);

	const getPlaceholder = () => {
		const selected = filterValues[filterKey]?.value || [];
		return selected.length === 0
			? t("label.quickFilter")
			: t("placeholder.filtersApplied", { count: selected.length });
	};

	// Don't render if no options are available
	if (availableOptions.length === 0) {
		return null;
	}

	return (
		<Grid item lg={3} md={6} xs={12}>
			<DyanamicDropdown
				dataSource={availableOptions}
				columns={[{ name: 'name', id: 1 }]}
				showHeader={false}
				width="100%"
				placeholder={getPlaceholder()}
				onChange={handleChange}
				selectdValueToshow="name"
				selectedValues={filterValues[filterKey]?.value}
				label={t("label.quickFilter")}
				icon={<FilterListIcon style={{ color: 'rgb(48,122,215)' }} />}
				required={false}
				showBorderColor
				multiSelect={true}
				maxDisplayItems={2}
				showSearchBox={false}
			/>
		</Grid>
	);
};
